import type { DataSourceEntity } from '@unifyapps/defs/types/dataSource';
import type { Schema } from '@unifyapps/network/generated/models/schema';
import { produce } from 'immer';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import createObjectSetters from '../../../utils/setters/createObjectSetters';
import type {
  DataSourceRecordStoreState,
  DataSourceRecordStoreType,
  UnsavedDataSource,
} from './types';

export const createDataSourceRecordStore = (initial?: Partial<DataSourceRecordStoreState>) =>
  create<DataSourceRecordStoreType>()(
    subscribeWithSelector((set) => {
      const {
        setMultiple: setDataSources,
        remove: removeDataSource,
        setSingle: setDataSource,
      } = createObjectSetters<DataSourceRecordStoreState, 'dataSources', DataSourceEntity>(
        'dataSources',
        set,
      );

      const { setMultiple: setDataSourceSchemas } = createObjectSetters<
        DataSourceRecordStoreState,
        'dataSourceSchemas',
        Schema
      >('dataSourceSchemas', set);

      const { setMultiple: setDataSourceSchemaErrors } = createObjectSetters<
        DataSourceRecordStoreState,
        'dataSourceSchemaErrors',
        string
      >('dataSourceSchemaErrors', set);

      const {
        setMultiple: setUnsavedDataSources,
        setSingle: setUnsavedDataSource,
        remove: removeUnsavedDataSource,
      } = createObjectSetters<DataSourceRecordStoreState, 'unsavedDataSources', UnsavedDataSource>(
        'unsavedDataSources',
        set,
      );

      // action to create a new data source with sync tracking
      const createDataSource = (
        id: string,
        dataSource: DataSourceEntity,
        skipSyncTracking = false,
      ) => {
        setDataSource(id, dataSource);

        if (!skipSyncTracking) {
          set(
            produce((draft) => {
              const sync = draft.sync;
              // mark as added
              sync.addedDataSources.push(dataSource);
            }),
          );
        }
      };

      // action to update an existing data source with sync tracking
      const updateDataSource = (
        id: string,
        dataSource: DataSourceEntity,
        skipSyncTracking = false,
      ) => {
        setDataSource(id, dataSource);

        if (!skipSyncTracking) {
          set(
            produce((draft) => {
              const sync = draft.sync;
              sync.dirtyDataSources.add(id);
            }),
          );
        }
      };

      // action to remove a data source with sync tracking
      const removeDataSourceWithSyncTracking = (id: string, skipSyncTracking = false) => {
        removeDataSource(id);

        if (!skipSyncTracking) {
          set(
            produce((draft) => {
              const sync = draft.sync;
              sync.deletedDataSources.push(id);
            }),
          );
        }
      };

      const storeObject: DataSourceRecordStoreType = {
        dataSources: initial?.dataSources ?? {},
        unsavedDataSources: initial?.unsavedDataSources ?? {},
        dataSourceSchemas: initial?.dataSourceSchemas ?? {},
        dataSourceSchemaErrors: initial?.dataSourceSchemaErrors ?? {},
        sync: {
          dirtyDataSources: new Set(),
          addedDataSources: [],
          deletedDataSources: [],
        },
        actions: {
          setDataSources,
          setDataSource, // keep for backward compatibility, but prefer createDataSource/updateDataSource
          createDataSource,
          updateDataSource,
          removeDataSource: removeDataSourceWithSyncTracking,
          setDataSourceSchemas,
          setDataSourceSchemaErrors,
          setUnsavedDataSources,
          setUnsavedDataSource,
          removeUnsavedDataSource,
        },
      };

      return storeObject;
    }),
  );
